import axios from "axios";

import { StatusCodes } from "../../@types/status-code";
import { NAVIGATION_ROUTES } from "../../routes";
import { logout as logoutAction } from "../../store/features/auth/authSlice";

import { makeStore } from "../../store/store";

import Authenticator from "./authenticator";
import { NETWORK_CONFIG } from "./config";
import TokenPersistor from "./persistor";

const handleTokenUpdate = (result) => {
  Authenticator.login(result);
};

const handleLogout = () => {
  try {
    const store = makeStore();
    const { dispatch } = store;
    dispatch(logoutAction());
    TokenPersistor.purge();
    window.location.replace(NAVIGATION_ROUTES.LOGIN);
  } catch (e) {
    console.error(e);
  }
};

let { response_formatter } = NETWORK_CONFIG;

let request_maker = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
});

request_maker.interceptors.request.use(
  (config) => {
    const token = TokenPersistor.read("access_token");
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }

    config.headers["Accept"] = "application/json";
    config.headers["Time-Stamp"] = Date.now();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshTokenPromise = null;

// const refreshToken = async () => {
//   try {
//     const refresh_token = TokenPersistor.read('refresh_token');
//     if (refresh_token) {
//       const resp = await request_maker.post('/auth/token/refresh/', {
//         refreshToken: refresh_token,
//       });

//       handleTokenUpdate({
//         accessToken: resp?.data?.data?.accessToken,
//         refreshToken: resp?.data?.data?.refreshToken,
//       });

//       return resp;
//     }
//   } catch (err) {
//     console.log({ err });
//     handleLogout();
//   }
// };

const refreshToken = async () => {
  try {
    const refresh_token = TokenPersistor.read("refresh_token");
    if (refresh_token) {
      const resp = await request_maker.post("/auth/token/refresh/", {
        refreshToken: refresh_token,
      });

      handleTokenUpdate({
        accessToken: resp?.data?.data?.accessToken,
        refreshToken: resp?.data?.data?.refreshToken,
      });

      return resp;
    } else {
      throw new Error("No refresh token found");
    }
  } catch (err) {
    console.error("Error refreshing token:", err);
    handleLogout(); // Call logout on any error in refreshToken
    throw err; // Propagate the error
  }
};

const MAX_RETRIES = 3;

request_maker.interceptors.response.use(
  (response) => response,
  async function (error) {
    const { config } = error;

    if (!config._retryCount) {
      config._retryCount = 0;
    }

    const { pathname } = window.location;
    const isAuthPage = ["/login", "/register", "/"].includes(pathname);
    if (isAuthPage) {
      return Promise.reject(error);
    }

    const { response: { status } = {} } = error;
    if (
      status === StatusCodes.UNAUTHORIZED &&
      config._retryCount < MAX_RETRIES
    ) {
      config._retryCount += 1;

      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenPromise = await refreshToken()
          .catch(() => {
            // If token refresh fails, logout and reject
            handleLogout();
            return Promise.reject(error);
          })
          .finally(() => {
            isRefreshing = false;
            refreshTokenPromise = null;
          });
      }

      await refreshTokenPromise;

      const newAccessToken = TokenPersistor.read("access_token");
      if (newAccessToken) {
        let updatedConfig = config;
        updatedConfig.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return request_maker(updatedConfig);
      } else {
        handleLogout();
        return Promise.reject(error);
      }
    }

    // if (config._retryCount < MAX_RETRIES && (!status || status >= 500)) {
    //   config._retryCount += 1;
    //   const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    //   await delay(1000 * config._retryCount); // Exponential backoff
    //   return request_maker(config);
    // }

    return Promise.reject(error);
  }
);

export { request_maker };
